// Document pointers, and the gates that check them.
//
// The OS holds a link and a signature state, never a file — signed documents live
// in Drive and stay there (R9). Every rule is in the database
// (20260829140000_documents_gates.sql): Aadhaar is absent from the allowed types
// by CHECK, an acknowledgement must name its version, and the gate refuses to
// answer anyone below people:full.
import { supabase } from './supabase';
import { write } from './write';

export type DocType =
  | 'offer_letter'
  | 'confirmation_notice'
  | 'ip_confidentiality'
  | 'handbook_ack'
  | 'pan'
  | 'qualification_cert'
  | 'bank_proof';

/**
 * The list the UI offers. It mirrors the column CHECK exactly — and Aadhaar is
 * absent from both, which is the point: R8.4 asks for that to be enforced by the
 * allowed types rather than left to instruction.
 */
export const DOC_TYPES: { id: DocType; label: string; triPart?: boolean }[] = [
  { id: 'offer_letter', label: 'Offer letter' },
  { id: 'confirmation_notice', label: 'Confirmation notice' },
  { id: 'ip_confidentiality', label: 'IP & confidentiality agreement', triPart: true },
  { id: 'handbook_ack', label: 'Handbook acknowledgement' },
  { id: 'pan', label: 'PAN' },
  { id: 'qualification_cert', label: 'Qualification certificate' },
  { id: 'bank_proof', label: 'Bank proof' },
];

export const docLabel = (t: string) =>
  DOC_TYPES.find((d) => d.id === t)?.label ?? t.replace(/_/g, ' ');

export type EmployeeDocument = {
  id: string;
  profile_id: string | null;
  candidate_email: string | null;
  doc_type: DocType;
  drive_link: string;
  version_ref: string | null;
  issued_on: string | null;
  signed_on: string | null;
  signed_by_company: boolean;
  signed_by_individual: boolean;
  witnessed: boolean;
};

export type RoleHandbook = {
  id: string;
  role_id: string;
  version: string;
  issued_on: string;
  drive_link: string;
};

export type Gate = {
  handbook_exists: boolean;
  handbook_version: string | null;
  ip_signed: boolean;
  ack_signed: boolean;
  exception: boolean;
  passed: boolean;
};

const DOC =
  'id, profile_id, candidate_email, doc_type, drive_link, version_ref, issued_on, ' +
  'signed_on, signed_by_company, signed_by_individual, witnessed';

type Result = { error: { message: string } | null };

/** A read's rows, or why there are none. A failed read is not an empty list
 *  (UI-CONVENTIONS §7): the caller renders LoadError on `error`, never "nothing yet". */
export type Read<T> = { data: T; error: { message: string } | null };

export async function listDocuments(profileId: string): Promise<Read<EmployeeDocument[]>> {
  if (!supabase) return { data: [], error: null };
  const { data, error } = await supabase
    .from('employee_document')
    .select(DOC)
    .eq('profile_id', profileId)
    .order('created_at', { ascending: false });
  return { data: (data as unknown as EmployeeDocument[]) ?? [], error };
}

export async function listHandbooks(): Promise<Read<RoleHandbook[]>> {
  if (!supabase) return { data: [], error: null };
  const { data, error } = await supabase
    .from('role_handbook')
    .select('id, role_id, version, issued_on, drive_link')
    .order('issued_on', { ascending: false });
  return { data: (data as RoleHandbook[]) ?? [], error };
}

export async function addDocument(input: {
  profileId?: string;
  candidateEmail?: string;
  docType: DocType;
  driveLink: string;
  versionRef?: string | null;
  signedByCompany?: boolean;
  signedByIndividual?: boolean;
  witnessed?: boolean;
}): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.from('employee_document').insert({
    profile_id: input.profileId ?? null,
    candidate_email: input.candidateEmail?.trim() || null,
    doc_type: input.docType,
    drive_link: input.driveLink.trim(),
    version_ref: input.versionRef ?? null,
    signed_by_company: input.signedByCompany ?? false,
    signed_by_individual: input.signedByIndividual ?? false,
    witnessed: input.witnessed ?? false,
  });
  return { error };
}

/**
 * A document's signature state — the only part of one that changes after it is
 * recorded (HRP-14: a countersignature arriving a week later had no way in, short
 * of recording the document twice). ed_write is people:full for every command,
 * so this needs no policy of its own. A zero-row update is a refusal, not a save.
 */
export async function updateDocument(
  id: string,
  patch: Partial<
    Pick<EmployeeDocument, 'signed_by_company' | 'signed_by_individual' | 'witnessed'>
  >,
): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const r = await write(supabase.from('employee_document').update(patch).eq('id', id));
  if (r.ok) return { error: null };
  return {
    error: r.reason === 'refused' ? { message: 'You can’t change this document.' } : r.error,
  };
}

/**
 * The papers every record needs, and whether this one has them — the same three
 * checks provisioning_gate() makes before an invite (20260829140000:177-198), read
 * off the rows already on screen so the Documents tab can say what is missing
 * rather than "Nothing recorded yet".
 */
export const requiredPapers = (
  rows: readonly EmployeeDocument[],
  handbook: RoleHandbook | null,
): { label: string; ok: boolean }[] => [
  { label: 'A current handbook for this role', ok: !!handbook },
  {
    label: 'IP & confidentiality signed by all three',
    ok: rows.some(
      (d) =>
        d.doc_type === 'ip_confidentiality' &&
        d.signed_by_company &&
        d.signed_by_individual &&
        d.witnessed,
    ),
  },
  {
    label: handbook
      ? `Handbook acknowledgement, version ${handbook.version}`
      : 'Handbook acknowledgement, current version',
    ok:
      !!handbook &&
      rows.some(
        (d) =>
          d.doc_type === 'handbook_ack' && d.version_ref === handbook.id && d.signed_by_individual,
      ),
  },
];

export async function addHandbook(input: {
  roleId: string;
  version: string;
  issuedOn: string;
  driveLink: string;
}): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.from('role_handbook').insert({
    role_id: input.roleId,
    version: input.version.trim(),
    issued_on: input.issuedOn,
    drive_link: input.driveLink.trim(),
  });
  return { error };
}

/** The three gates, for the invite modal. Returns null when not connected. */
export async function checkGate(email: string, roleId: string): Promise<Gate | null> {
  if (!supabase || !email.trim() || !roleId) return null;
  const { data, error } = await supabase.rpc('provisioning_gate', {
    p_email: email,
    p_role_id: roleId,
  });
  if (error) return null;
  return data as Gate;
}

/**
 * The only way past a closed gate (R8.2). Not an argument to the gate function —
 * the exception row IS the override, and it carries a reason and a named
 * approver like every other one.
 */
export async function bypassGate(email: string, reason: string): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.from('hr_exception').insert({
    subject_type: 'provisioning',
    subject_id: email.trim().toLowerCase(),
    action: 'bypassed a provisioning gate',
    standard_value: 'paperwork complete before a login exists',
    actual_value: 'invited with the paperwork outstanding',
    reason: reason.trim(),
  });
  return { error };
}
